import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  TextField,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  InputAdornment,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Code as CodeIcon,
  FileUpload as FileUploadIcon,
  Assessment as AssessmentIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import api from '../../../api';
import { showSuccess, showError, showDeleteConfirmation } from '../../../utils/sweetAlertConfig';
import PageHeader from '../../../components/PageHeader';
import ModernTable from '../../../components/ModernTable';
import StatsCard from '../../../components/StatsCard';

const PRIMARY = "#29204C";
const SUCCESS = "#68B977";
const SECONDARY = "#5A6D8B";

export default function TechnicalAssessments() {
  const [technicalAssessments, setTechnicalAssessments] = useState([]);
  const [allTechnicalAssessments, setAllTechnicalAssessments] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [formData, setFormData] = useState({
    title: '',
    job_id: '',
    description: '',
    instructions: '',
    submission_type: 'file',
    max_file_size_mb: 10,
    allowed_file_types: 'jpg,jpeg,png,pdf,zip,doc,docx',
    deadline_hours: 120,
    max_score: 100,
    is_active: true
  });

  // Stats state
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    avgScore: 0,
  });

  useEffect(() => {
    fetchTechnicalAssessments();
    fetchJobs();
  }, []);

  const fetchTechnicalAssessments = () => {
    setLoading(true);
    api.get('/technical-assessments')
      .then(res => {
        const data = res.data.technical_assessments || [];
        setTechnicalAssessments(data);
        setAllTechnicalAssessments(data);
        calculateStats(data);
      })
      .catch(() => setError("Failed to fetch technical assessments."))
      .finally(() => setLoading(false));
  };

  const fetchJobs = async () => {
    try {
      const response = await api.get('/assessments-data/jobs');
      setJobs(response.data || []);
    } catch (error) {
      try {
        const fallbackResponse = await api.get('/technical-assessments/jobs');
        setJobs(fallbackResponse.data.jobs || []);
      } catch (fallbackError) {
        console.error('Failed to fetch jobs:', fallbackError);
        setJobs([]);
      }
    }
  };

  const calculateStats = (assessmentsData) => {
    setStats({
      total: assessmentsData.length,
      active: assessmentsData.filter(a => a.is_active).length,
      inactive: assessmentsData.filter(a => !a.is_active).length,
      avgScore: assessmentsData.length > 0 ? Math.round(assessmentsData.reduce((sum, a) => sum + (a.max_score || 0), 0) / assessmentsData.length) : 0,
    });
  };

  useEffect(() => {
    if (!search) {
      setTechnicalAssessments(allTechnicalAssessments);
    } else {
      const val = search.toLowerCase();
      setTechnicalAssessments(allTechnicalAssessments.filter(a =>
        (a.title && a.title.toLowerCase().includes(val)) ||
        (a.job_title && a.job_title.toLowerCase().includes(val)) ||
        (a.submission_type && a.submission_type.toLowerCase().includes(val))
      ));
    }
    setPage(0);
  }, [search, allTechnicalAssessments]);

  const handleEdit = (row) => {
    setEditingAssessment(row);
    setFormData({
      title: row.title,
      job_id: row.job_id,
      description: row.description,
      instructions: row.instructions || '',
      submission_type: row.submission_type,
      max_file_size_mb: row.max_file_size_mb,
      allowed_file_types: row.allowed_file_types,
      deadline_hours: row.deadline_hours,
      max_score: row.max_score,
      is_active: Boolean(row.is_active)
    });
    setShowModal(true);
  };

  const handleDelete = (row) => {
    showDeleteConfirmation(
      'Are you sure?',
      `Do you want to delete technical assessment "${row.title}"?`
    ).then((result) => {
      if (result.isConfirmed) {
        api.delete(`/technical-assessments/${row.technical_assessment_id}`)
          .then(() => {
            showSuccess('Deleted!', 'Technical assessment has been deleted successfully.');
            fetchTechnicalAssessments();
          })
          .catch(() => {
            showError('Error!', 'Failed to delete technical assessment. Please try again.');
          });
      }
    });
  };

  const handleAddNew = () => {
    setEditingAssessment(null);
    setFormData({
      title: '',
      job_id: '',
      description: '',
      instructions: '',
      submission_type: 'file',
      max_file_size_mb: 10,
      allowed_file_types: 'jpg,jpeg,png,pdf,zip,doc,docx',
      deadline_hours: 120,
      max_score: 100,
      is_active: true
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingAssessment) {
        await api.put(`/technical-assessments/${editingAssessment.technical_assessment_id}`, formData);
        showSuccess('Updated!', 'Technical assessment updated successfully!');
      } else {
        await api.post('/technical-assessments', formData);
        showSuccess('Created!', 'Technical assessment created successfully!');
      }

      setShowModal(false);
      fetchTechnicalAssessments();
    } catch (error) {
      showError('Error!', 'Failed to save technical assessment. Please try again.');
    }
  };

  const getSubmissionTypeColor = (type) => {
    switch(type) {
      case 'file': return '#2196f3';
      case 'image': return '#00bcd4';
      case 'text': return SUCCESS;
      case 'code': return '#ff9800';
      case 'zip': return '#9c27b0';
      default: return SECONDARY;
    }
  };

  const columns = [
    {
      label: "Title",
      field: "title",
      render: (row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.title}
          </Typography>
          {row.job_title && (
            <Typography variant="caption" color="text.secondary">
              {row.job_title}
            </Typography>
          )}
          <Typography variant="caption" display="block" color="text.secondary">
            ID: {row.technical_assessment_id}
          </Typography>
        </Box>
      ),
    },
    {
      label: "Submission Type",
      field: "submission_type",
      align: "center",
      width: "150px",
      render: (row) => (
        <Chip
          label={row.submission_type ? row.submission_type.toUpperCase() : 'FILE'}
          sx={{
            bgcolor: getSubmissionTypeColor(row.submission_type),
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
          size="small"
        />
      ),
    },
    {
      label: "Details",
      field: "max_file_size_mb",
      width: "150px",
      render: (row) => (
        <Box>
          <Typography variant="caption" display="block">
            Max: {row.max_file_size_mb}MB
          </Typography>
          <Typography variant="caption" display="block">
            Deadline: {row.deadline_hours}min
          </Typography>
        </Box>
      ),
    },
    {
      label: "Score",
      field: "max_score",
      align: "center",
      width: "100px",
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: PRIMARY, fontSize: '1.1rem' }}>
          {row.max_score}
        </Typography>
      ),
    },
    {
      label: "Status",
      field: "is_active",
      align: "center",
      width: "120px",
      render: (row) => (
        <Chip
          label={row.is_active ? 'ACTIVE' : 'INACTIVE'}
          sx={{
            bgcolor: row.is_active ? SUCCESS : '#6c757d',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
          size="small"
        />
      ),
    },
  ];

  const paginatedAssessments = technicalAssessments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Technical Assessments"
        subtitle="Manage coding challenges and practical tasks"
        icon={CodeIcon}
        breadcrumbs={[
          { label: 'Dashboard', link: '/dashboard' },
          { label: 'Technical Assessments' }
        ]}
        action={{
          label: 'Add Technical Assessment',
          icon: <AddIcon />,
          onClick: handleAddNew,
          variant: 'contained',
          color: 'success',
        }}
      />

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Assessments"
            value={stats.total}
            icon={CodeIcon}
            color={PRIMARY}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Active"
            value={stats.active}
            icon={ActiveIcon}
            color={SUCCESS}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Inactive"
            value={stats.inactive}
            icon={InactiveIcon}
            color="#6c757d"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Avg Score"
            value={stats.avgScore}
            icon={AssessmentIcon}
            color="#2196f3"
          />
        </Grid>
      </Grid>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by title, job, or submission type..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 400,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#fff',
            }
          }}
        />
      </Box>

      {/* Table */}
      <ModernTable
        columns={columns}
        data={paginatedAssessments}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRows={technicalAssessments.length}
        onPageChange={setPage}
        onRowsPerPageChange={(newRowsPerPage) => {
          setRowsPerPage(newRowsPerPage);
          setPage(0);
        }}
        onEdit={handleEdit}
        onDelete={handleDelete}
        emptyMessage={error || "No technical assessments found"}
      />

      {/* Add/Edit Modal */}
      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: PRIMARY,
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '1.25rem',
          }}
        >
          {editingAssessment ? 'Edit Technical Assessment' : 'Add New Technical Assessment'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="e.g., Build a Todo App"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required>
                  <InputLabel>Job Position</InputLabel>
                  <Select
                    value={formData.job_id}
                    label="Job Position"
                    onChange={(e) => setFormData({ ...formData, job_id: e.target.value })}
                  >
                    <MenuItem value="">Select Job</MenuItem>
                    {jobs.map(job => (
                      <MenuItem key={job.job_id} value={job.job_id}>
                        {job.posting_title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={3}
                  required
                  placeholder="Brief description of the task..."
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Instructions"
                  value={formData.instructions}
                  onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                  multiline
                  rows={4}
                  placeholder="Detailed instructions for completing the task..."
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl fullWidth required>
                  <InputLabel>Submission Type</InputLabel>
                  <Select
                    value={formData.submission_type}
                    label="Submission Type"
                    onChange={(e) => setFormData({ ...formData, submission_type: e.target.value })}
                  >
                    <MenuItem value="file">File Upload</MenuItem>
                    <MenuItem value="image">Image</MenuItem>
                    <MenuItem value="text">Text</MenuItem>
                    <MenuItem value="code">Code</MenuItem>
                    <MenuItem value="zip">ZIP</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Max File Size (MB)"
                  type="number"
                  inputProps={{ min: 1, max: 100 }}
                  value={formData.max_file_size_mb}
                  onChange={(e) => setFormData({ ...formData, max_file_size_mb: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Deadline (Minutes)"
                  type="number"
                  inputProps={{ min: 1, max: 10080 }}
                  value={formData.deadline_hours}
                  onChange={(e) => setFormData({ ...formData, deadline_hours: e.target.value })}
                  placeholder="e.g., 120 for 2 hours"
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Max Score"
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={formData.max_score}
                  onChange={(e) => setFormData({ ...formData, max_score: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Allowed File Types"
                  value={formData.allowed_file_types}
                  onChange={(e) => setFormData({ ...formData, allowed_file_types: e.target.value })}
                  placeholder="jpg,jpeg,png,pdf,zip,doc,docx"
                  helperText="Comma-separated file extensions (e.g., jpg,png,pdf,zip)"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  }
                  label="Active (visible to candidates)"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button
              onClick={() => setShowModal(false)}
              variant="outlined"
              sx={{ color: SECONDARY, borderColor: SECONDARY }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                bgcolor: SUCCESS,
                '&:hover': { bgcolor: '#5aa868' },
              }}
            >
              {editingAssessment ? 'Update Assessment' : 'Create Assessment'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
}
